import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { getBlockColor as defaultGetBlockColor, getEmissiveColor as defaultGetEmissiveColor, TETROMINO_COLORS } from './materials/SpectrumMaterial';

const BOARD_ROWS = 20;
const BOARD_COLUMNS = 10;
const MAX_BLOCKS = BOARD_ROWS * BOARD_COLUMNS; // 200 blocks max
const BLOCK_SIZE = 0.88;
const BLOCK_DEPTH = BLOCK_SIZE * 0.75;

// Shared geometries - created once, reused for all instances
const blockGeometry = new THREE.BoxGeometry(BLOCK_SIZE, BLOCK_SIZE, BLOCK_DEPTH);
const glowPlaneGeometry = new THREE.PlaneGeometry(0.7, 0.7);
const outerGlowGeometry = new THREE.BoxGeometry(BLOCK_SIZE * 1.2, BLOCK_SIZE * 1.2, BLOCK_DEPTH * 0.6);

// Temp objects for matrix calculations (reused to avoid GC)
const tempMatrix = new THREE.Matrix4();
const tempPosition = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();
const tempScale = new THREE.Vector3(1, 1, 1);
const zeroScale = new THREE.Vector3(0, 0, 0);

// Create materials for a specific color
const createMaterialsForColor = (baseColor, emissiveColor) => ({
  main: new THREE.MeshStandardMaterial({
    color: new THREE.Color(baseColor),
    emissive: new THREE.Color(emissiveColor),
    emissiveIntensity: 0.7,
    metalness: 0.4,
    roughness: 0.15,
    side: THREE.DoubleSide,
  }),
  glow: new THREE.MeshBasicMaterial({
    color: new THREE.Color(baseColor),
    opacity: 0.35,
    transparent: true,
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }),
  innerGlow: new THREE.MeshBasicMaterial({
    color: new THREE.Color(baseColor),
    transparent: true,
    opacity: 0.25,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  }),
});

/**
 * InstancedBoard - GPU-instanced rendering of all placed blocks
 *
 * Instead of creating 200 individual Mesh objects, we use InstancedMesh
 * which sends all block data to the GPU in a single draw call per color.
 */
const InstancedBoard = ({
  boardMatrix,
  boardVersion = 0,
  getBlockColorFn = defaultGetBlockColor,
  getEmissiveColorFn = defaultGetEmissiveColor,
  themeColors = TETROMINO_COLORS
}) => {
  // Refs for instanced meshes - one per color for proper materials
  const instanceRefs = useRef({});
  const glowInstanceRefs = useRef({});
  const innerGlowRefs = useRef({});

  // Track block counts per color
  const blockCountsRef = useRef({});

  // Time uniform for shader animations
  const timeRef = useRef(0);

  // Create instanced meshes for each color
  const colorKeys = useMemo(() => Object.keys(themeColors), [themeColors]);

  // Create material pool based on current theme
  const materialPool = useMemo(() => {
    const pool = {};
    colorKeys.forEach(colorClass => {
      const baseColor = getBlockColorFn(colorClass);
      const emissiveColor = getEmissiveColorFn(colorClass);
      pool[colorClass] = createMaterialsForColor(baseColor, emissiveColor);
    });
    return pool;
  }, [colorKeys, getBlockColorFn, getEmissiveColorFn]);

  // Helper to initialize a mesh by hiding all instances
  const initializeMesh = (mesh) => {
    if (!mesh) return;
    tempMatrix.compose(tempPosition, tempQuaternion, zeroScale);
    for (let i = 0; i < MAX_BLOCKS; i++) {
      mesh.setMatrixAt(i, tempMatrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  };

  // Ref callbacks that initialize meshes when they're first created
  const setMainMeshRef = (colorClass) => (el) => {
    if (el && !instanceRefs.current[colorClass]) {
      instanceRefs.current[colorClass] = el;
      initializeMesh(el);
    } else if (el) {
      instanceRefs.current[colorClass] = el;
    }
  };

  const setGlowMeshRef = (colorClass) => (el) => {
    if (el && !glowInstanceRefs.current[colorClass]) {
      glowInstanceRefs.current[colorClass] = el;
      initializeMesh(el);
    } else if (el) {
      glowInstanceRefs.current[colorClass] = el;
    }
  };

  const setInnerGlowMeshRef = (colorClass) => (el) => {
    if (el && !innerGlowRefs.current[colorClass]) {
      innerGlowRefs.current[colorClass] = el;
      initializeMesh(el);
    } else if (el) {
      innerGlowRefs.current[colorClass] = el;
    }
  };

  // Get invalidate for demand-mode rendering (opponents)
  const { invalidate } = useThree();

  // Update instances when board changes
  useEffect(() => {
    if (!boardMatrix) return;

    // Reset block counts
    colorKeys.forEach(colorClass => {
      blockCountsRef.current[colorClass] = 0;
    });

    // Collect blocks by color
    const blocksByColor = {};
    colorKeys.forEach(colorClass => {
      blocksByColor[colorClass] = [];
    });

    // Parse board matrix - check for valid cells
    // Board matrix may have extra column for line count tracking
    for (let row = 0; row < boardMatrix.length; row++) {
      const rowData = boardMatrix[row];
      if (!rowData) continue;

      // Only check columns 0 to BOARD_COLUMNS-1 (skip any extra columns)
      const colLimit = Math.min(rowData.length, BOARD_COLUMNS);
      for (let col = 0; col < colLimit; col++) {
        const cell = rowData[col];
        // Check if cell is a valid tetromino color class (string, not empty, not 0)
        if (cell && cell !== 0 && cell !== '' && typeof cell === 'string') {
          if (blocksByColor[cell]) {
            blocksByColor[cell].push({ row, col });
          }
        }
      }
    }

    // Update instanced meshes for each color
    let meshesUpdated = 0;

    colorKeys.forEach(colorClass => {
      const blocks = blocksByColor[colorClass];
      const mainMesh = instanceRefs.current[colorClass];
      const glowMesh = glowInstanceRefs.current[colorClass];
      const innerGlowMesh = innerGlowRefs.current[colorClass];

      if (!mainMesh) return;
      meshesUpdated++;

      blockCountsRef.current[colorClass] = blocks.length;

      // Update each block instance
      blocks.forEach((block, index) => {
        const x = block.col - (BOARD_COLUMNS / 2) + 0.5;
        const y = (BOARD_ROWS - block.row) - 0.5;

        // Set position for main block and glow
        tempPosition.set(x, y, 0);
        tempMatrix.compose(tempPosition, tempQuaternion, tempScale);

        // Update main block
        mainMesh.setMatrixAt(index, tempMatrix);

        // Update outer glow (same position)
        if (glowMesh) {
          glowMesh.setMatrixAt(index, tempMatrix);
        }

        // Update inner glow (offset in Z)
        if (innerGlowMesh) {
          tempPosition.set(x, y, 0.35);
          tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
          innerGlowMesh.setMatrixAt(index, tempMatrix);
        }
      });

      // Hide unused instances by scaling to 0
      for (let i = blocks.length; i < MAX_BLOCKS; i++) {
        tempMatrix.compose(tempPosition, tempQuaternion, zeroScale);
        mainMesh.setMatrixAt(i, tempMatrix);
        if (glowMesh) glowMesh.setMatrixAt(i, tempMatrix);
        if (innerGlowMesh) innerGlowMesh.setMatrixAt(i, tempMatrix);
      }

      // Mark matrices as needing update
      mainMesh.instanceMatrix.needsUpdate = true;
      if (glowMesh) glowMesh.instanceMatrix.needsUpdate = true;
      if (innerGlowMesh) innerGlowMesh.instanceMatrix.needsUpdate = true;
    });

    // Force frame update for demand-mode rendering (opponents use frameloop="demand")
    if (meshesUpdated > 0) {
      invalidate();
    }
  }, [boardMatrix, boardVersion, colorKeys, invalidate]);

  // Animate glow effects - single update for all materials (not per-block!)
  useFrame((state) => {
    timeRef.current = state.clock.elapsedTime;

    // Animate glow opacity for all colors at once - ONE calculation, not 200
    const glowPulse = 0.35 + Math.sin(timeRef.current * 1.5) * 0.1;
    const innerPulse = 0.25 + Math.sin(timeRef.current * 1.5) * 0.1;

    colorKeys.forEach(colorClass => {
      const materials = materialPool[colorClass];
      if (materials) {
        materials.glow.opacity = glowPulse;
        materials.innerGlow.opacity = innerPulse;
      }
    });
  });

  return (
    <group>
      {colorKeys.map(colorClass => {
        const materials = materialPool[colorClass];
        if (!materials) return null;

        return (
          <group key={colorClass}>
            {/* Main block instances - ONE draw call for all blocks of this color */}
            <instancedMesh
              ref={setMainMeshRef(colorClass)}
              args={[blockGeometry, materials.main, MAX_BLOCKS]}
              castShadow
              receiveShadow
              frustumCulled={false}
            />

            {/* Outer glow instances */}
            <instancedMesh
              ref={setGlowMeshRef(colorClass)}
              args={[outerGlowGeometry, materials.glow, MAX_BLOCKS]}
              frustumCulled={false}
            />

            {/* Inner glow plane instances */}
            <instancedMesh
              ref={setInnerGlowMeshRef(colorClass)}
              args={[glowPlaneGeometry, materials.innerGlow, MAX_BLOCKS]}
              frustumCulled={false}
            />
          </group>
        );
      })}
    </group>
  );
};

export default InstancedBoard;
